import { computed } from 'vue';
import { useAvailability } from '@common-vue/media-card-large';
import { useProductFlags, useTopLevelCategoryFlags } from '@modules/featureControl';
import { useType } from '@modules/types';
import { useProduct } from '@modules/product';
import { useSize } from '@modules/size/composables';
import { useUpc } from '@modules/upc';
import { isAnyUpcAvailable } from '../utils/utils';

function typeDrivenMapper(availability, types) {
  return types?.map((type) => ({ ...type, available: isAnyUpcAvailable(availability, { typeId: type.id }) })) || [];
}

export default function useTypeVariant() {
  const { isTypeDrivenThirdVariantFlag: isTypeDriven } = useProductFlags();
  const { isBeddingCategory } = useTopLevelCategoryFlags();
  const { productId, productData: product } = useProduct();
  const { selectedSizeId } = useSize();
  const {
    type: typeAvailability,
  } = useAvailability(productId);
  const { traitsMap } = useUpc();

  /**
   * @type {import('vue').ComputedRef<Type[]>}
   */
  const filteredTypes = computed(() => {
    const { types } = useType();

    if (isTypeDriven) {
      return typeDrivenMapper(traitsMap.value, types.value);
    }

    if (isBeddingCategory) {
      return types.value;
    }

    const size = product.traits.sizes.sizeMap[selectedSizeId.value ?? -1] || { typeToColors: {} };
    const typeIdsForSize = Object.keys(size.typeToColors).map((id) => parseInt(id, 10)) || [];

    return types.value.filter((type) => typeIdsForSize.includes(type.id));
  });

  const availableTypes = computed(() => filteredTypes.value.filter((type) => typeAvailability.value[type.id]));

  return {
    filteredTypes,
    availableTypes,
  };
}
